import { Playground, PLAYGROUNG_BG_LIGHTER } from '../../../components/playground/Playground';
import {
    PlaygroundClasses,
    PlaygroundClassesSpacer,
    PlaygroundNavClasses,
} from '../../../components/playground/PlaygroundClasses';
import { Description } from '../../../components/Description';
import { Title } from '../../../components/Title';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import Select from '@rio-cloud/rio-uikit/Select';

export const Pills = () => (
    <div>
        <Description headline='Pills' anchor='pills' />
        <Playground example={pillsExample} classes={pillsClasses} />
        <Playground example={pillsFilledExample} classes={pillsClasses} />
        <Playground example={pillsStackedExample} classes={pillsClasses} />
        <Description headline='Condensed filled nav-pills as view toggle' headlineType='h5'>
            For use next to form elements like buttons and selects. This keeps heights aligned and provides a clearer
            way to indicate different views. Prefer these over the toggle button group for view switching.
        </Description>
        <Playground example={pillsFilledTableExample} classes={pillsClasses} background={PLAYGROUNG_BG_LIGHTER} />
    </div>
);

const pillsExample = (
    <div className='display-grid grid-cols-1 grid-cols-2-sm column-gap-25 row-gap-50'>
        <div>
            <Title>Standard</Title>
            <ul className='nav nav-pills'>
                <li className='active'>
                    <span>Pill 1</span>
                </li>
                <li>
                    <span>Pill 2</span>
                </li>
                <li>
                    <span>Pill 3</span>
                </li>
                <SimpleTooltip content='This item is disabled' placement='bottom'>
                    <li className='disabled'>
                        <span>Disabled Pill</span>
                    </li>
                </SimpleTooltip>
            </ul>
        </div>

        <div>
            <Title>Justified</Title>
            <ul className='nav nav-pills nav-justified'>
                <li className='active'>
                    <span>First tab</span>
                </li>
                <li>
                    <span>Second tab with longer content</span>
                </li>
                <li>
                    <span>Third tab</span>
                </li>
                <li>
                    <span>Fourth tab</span>
                </li>
            </ul>
        </div>

        <div>
            <Title>Justified with icons</Title>
            <ul className='nav nav-pills'>
                <li className='active'>
                    <div className='display-flex flex-row gap-5 align-items-center'>
                        <span className='rioglyph rioglyph-warning-sign text-color-warning' />
                        <span>Pill 1</span>
                    </div>
                </li>
                <li>
                    <div className='display-flex flex-row gap-5 align-items-center'>
                        <span className='rioglyph rioglyph-envelope' />
                        <span>Pill 2</span>
                    </div>
                </li>
                <li>
                    <div className='display-flex flex-row gap-5 align-items-center'>
                        <span className='rioglyph rioglyph-files' />
                        <span>Pill 3</span>
                    </div>
                </li>
                <li className='disabled'>
                    <div className='display-flex flex-row gap-5 align-items-center'>
                        <span className='rioglyph rioglyph-download' />
                        <span>Disabled Pill</span>
                    </div>
                </li>
            </ul>
        </div>

        <div>
            <Title>Justified with word-wrap</Title>
            <ul className='nav nav-pills nav-justified nav-justified-word-wrap'>
                <li className='active'>
                    <span>First tab</span>
                </li>
                <li>
                    <span>Second tab with longer content</span>
                </li>
                <li>
                    <span>Third tab</span>
                </li>
                <li>
                    <span>Fourth tab</span>
                </li>
            </ul>
        </div>
    </div>
);

const pillsFilledExample = (
    <div className='display-grid grid-cols-1 grid-cols-2-sm column-gap-25 row-gap-50'>
        <div>
            <Title>Filled</Title>
            <ul className='nav nav-pills nav-pills-filled nav-justified'>
                <li className='active'>
                    <span>First tab</span>
                </li>
                <li>
                    <span>Second tab with longer content</span>
                </li>
                <li>
                    <span>Third tab</span>
                </li>
                <li className='disabled'>
                    <span>Fourth tab</span>
                </li>
            </ul>
        </div>

        <div>
            <Title>Filled with icons only</Title>
            <ul className='rounded-none nav nav-pills nav-pills-filled nav-justified text-size-18'>
                <li className='active'>
                    <span className='rioglyph rioglyph-pushpin' />
                </li>
                <li className=''>
                    <span className='rioglyph rioglyph-geofence' />
                </li>
                <li className=''>
                    <span className='rioglyph rioglyph-workshop' />
                </li>
                <li className=''>
                    <span className='rioglyph rioglyph-filling-e-station' />
                </li>
                <li className=''>
                    <span className='rioglyph rioglyph-parking' />
                </li>
            </ul>
        </div>

        <div>
            <Title>Filled condensed (for use with other form elements)</Title>
            <div className='max-width-350'>
                <ul className='nav nav-pills nav-pills-filled nav-pills-filled-condensed nav-justified shadow-subtle'>
                    <li className='active'>
                        <div className='display-flex flex-row align-items-center gap-5'>
                            <span className='rioglyph rioglyph-parcel' />
                            <span>Shipments</span>
                        </div>
                    </li>
                    <li>
                        <div className='display-flex flex-row align-items-center gap-5'>
                            <span className='rioglyph rioglyph-truck' />
                            <span>Vehicles</span>
                        </div>
                    </li>
                    <li>
                        <div className='display-flex flex-row align-items-center gap-5'>
                            <span className='rioglyph rioglyph-trailer' />
                            <span>Trailer</span>
                            <div>
                                <span className='badge badge-muted'>5</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
);

const pillsFilledTableExample = (
    <div>
        <div className='table-toolbar'>
            <div className='table-toolbar-container'>
                <div className='table-toolbar-group-left'>
                    <div className='table-toolbar-column'>
                        <ul className='nav nav-pills nav-pills-filled nav-pills-filled-condensed'>
                            <li className='active'>
                                <span className='display-flex flex-row align-items-center gap-5'>
                                    <span className='rioglyph rioglyph-parcel' />
                                    <span>Shipments</span>
                                </span>
                            </li>
                            <li>
                                <span className='display-flex flex-row align-items-center gap-5'>
                                    <span className='rioglyph rioglyph-truck' />
                                    <span>Vehicles</span>
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div className='table-toolbar-column'>
                        <Select
                            id='2'
                            className='min-width-200'
                            placeholder='Please select'
                            options={[
                                { id: '1', label: 'Option 1' },
                                { id: '2', label: 'Option 2' },
                            ]}
                        />
                    </div>
                </div>

                <div className='table-toolbar-group-right'>
                    <div className='table-toolbar-column'>
                        <TableSearch placeholder='Search in table' />
                    </div>
                </div>
            </div>
        </div>
        <div className='table-responsive'>
            <table className='table table-hover table-head-filled table-bordered'>
                <thead>
                    <tr>
                        <th>Lane</th>
                        <th>Load ID</th>
                        <th>ETA</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Rotterdam → Munich</td>
                        <td>LD-48271</td>
                        <td>10:40</td>
                        <td>In transit</td>
                    </tr>
                    <tr>
                        <td>Hamburg → Lyon</td>
                        <td>LD-51723</td>
                        <td>12:15</td>
                        <td>At cross-dock</td>
                    </tr>
                    <tr>
                        <td>Milan → Zurich</td>
                        <td>LD-60018</td>
                        <td>14:05</td>
                        <td>Delivered</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);

const pillsStackedExample = (
    <div className='display-grid grid-cols-1 grid-cols-2-sm column-gap-25 row-gap-50'>
        <div>
            <Title>Stacked</Title>
            <ul className='nav nav-pills nav-stacked'>
                <li className='active'>
                    <span>Stacked pill 1</span>
                </li>
                <li>
                    <span>Stacked pill 2</span>
                </li>
                <li>
                    <span>Stacked pill 3</span>
                </li>
                <li className='disabled'>
                    <span>Disabled stacked pill</span>
                </li>
            </ul>
        </div>

        <div>
            <Title>Stacked decent</Title>
            <ul className='nav nav-pills nav-pills-stacked-decent nav-stacked nav-stacked-decent'>
                <li className='active'>
                    <span>Stacked pill 1</span>
                </li>
                <li>
                    <span>Stacked pill 2</span>
                </li>
                <li>
                    <span>Stacked pill 3</span>
                </li>
                <li className='disabled'>
                    <span>Disabled stacked pill</span>
                </li>
            </ul>
        </div>

        <div>
            <Title>Stacked decent menu</Title>
            <ul className='nav nav-pills nav-pills-stacked-decent nav-stacked nav-stacked-decent'>
                <li className='menu-item active'>
                    <a href='/#'>
                        <div className='flex-1-1 display-flex align-items-center gap-10 padding-y-2'>
                            <div className='position-relative'>
                                <span className='text-size-h4 text-color-primary rioglyph rioglyph-user' />
                            </div>
                            <div className='line-height-125rel'>
                                <div className='text-size-16'>Lorem ipsum</div>
                                <div className='text-size-12 text-color-dark'>Dolor sit amet</div>
                            </div>
                        </div>
                    </a>
                </li>
                <li className='menu-item'>
                    <a href='/#'>
                        <div className='flex-1-1 display-flex align-items-center gap-10 padding-y-2'>
                            <div className='position-relative'>
                                <span className='text-size-h4 text-color-primary rioglyph rioglyph-addressbook' />
                            </div>
                            <div className='line-height-125rel'>
                                <div className='text-size-16'>Lorem ipsum</div>
                                <div className='text-size-12 text-color-dark'>Dolor sit amet</div>
                            </div>
                        </div>
                    </a>
                </li>
                <li className='menu-item'>
                    <a href='/#'>
                        <div className='flex-1-1 display-flex align-items-center gap-10 padding-y-2'>
                            <div className='position-relative'>
                                <span className='text-size-h4 text-color-primary rioglyph rioglyph-book' />
                            </div>
                            <div className='line-height-125rel'>
                                <div className='text-size-16'>Lorem ipsum</div>
                                <div className='text-size-12 text-color-dark'>Dolor sit amet</div>
                            </div>
                        </div>
                    </a>
                </li>
                <li className='menu-item disabled'>
                    <a href='/#'>
                        <div className='flex-1-1 display-flex align-items-center gap-10 padding-y-2'>
                            <div className='position-relative'>
                                <span className='text-size-h4 text-color-light rioglyph rioglyph-api' />
                            </div>
                            <div className='line-height-125rel'>
                                <div className='text-size-16'>Lorem ipsum</div>
                                <div className='text-size-12 text-color-light'>Dolor sit amet</div>
                            </div>
                        </div>
                    </a>
                </li>
            </ul>
        </div>
    </div>
);

const pillsClasses = (
    <>
        <PlaygroundNavClasses showNavVariants />
        <PlaygroundClassesSpacer label='Nav pills' />
        <PlaygroundClasses label='Wrapper' classes='nav nav-pills' />
        <PlaygroundClasses
            label='Variants'
            classes={['nav-pills-filled', 'nav-pills-filled-condensed', 'nav-pills-stacked-decent']}
        />
    </>
);
